import { router, usePage } from "@inertiajs/react";
import { t } from "@travily/i18n";
import {
  accountPayoutsPath,
  creatorListingsPath,
  destinationsPath,
  loginPath,
  marketplaceIndexPath,
  messagesPath,
  notificationsPath,
  preferencesPath,
  programsPath,
  sessionPath,
} from "@travily/routes";
import { Alert } from "@ui/components/Alert";
import { DropMenuPopover } from "@ui/components/DropMenu";
import { Footer } from "@ui/components/Footer";
import { Frame, type FrameContentMaxWidth } from "@ui/components/Frame";
import { Icon } from "@ui/components/Icon";
import { outlineButton } from "@ui/components/OutlineButton";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import { TopBarWeb } from "@ui/components/TopBar/TopBarWeb";
import { TopBarNavLink } from "@ui/components/TopBar/TopBarWeb/components/TopBarNavLink";
import { configureDisplayCurrency } from "@ui/lib/format-price";
import { getCurrencyLabel, getLocaleLabel } from "@ui/lib/locale-data";
import { Button } from "@ui/primitives/Button";
import { Link } from "@ui/primitives/Link";
import { MenuTrigger } from "@ui/primitives/Menu";
import { Children, isValidElement, type ReactElement, type ReactNode, useCallback, useState } from "react";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";
import { type AccountSwitcherProps, AppMenu } from "@/components/AppMenu/AppMenu";
import { LocaleModal, type LocaleModalTab } from "@/components/LocaleModal/LocaleModal";
import { MessagesAction } from "@/components/MessagesAction/MessagesAction";
import { NotificationBell } from "@/components/NotificationBell/NotificationBell";
import type { NotificationAction } from "@/components/NotificationBell/NotificationItem";
import { NotificationsAction } from "@/components/NotificationsAction/NotificationsAction";
import { PersonAvatar } from "@/components/PersonAvatar/PersonAvatar";
import { useFlashAlerts } from "@/hooks/use-flash-alerts";
import { useIntercom } from "@/hooks/use-intercom";
import { useNotificationChannel } from "@/hooks/use-notification-channel";
import { useAccountSwitcher } from "@/layouts/components/account-switcher";
import { AppBottomBar } from "@/layouts/components/app-bottom-bar";
import { isExploreSection, isNavActive, isRootRoute } from "@/layouts/components/bottom-tabs";
import { ExploreSearchOverlay } from "@/layouts/components/explore-search-overlay";
import { ExploreSearchTopBar } from "@/layouts/components/explore-search-top-bar";
import type { SharedProps } from "@/types";

export interface PageFrameProps {
  /** Decorative full-bleed background — passed to `Frame.Background`. */
  background?: ReactNode;
  /** Mode-aware bottom-bar / top-bar selection. */
  mode?: "explorer" | "creator";
  /** Override the default mobile top bar. When omitted, PageFrame renders a root
   *  `TopBarMobile` with the global MessagesAction. Pass a custom `<TopBarMobile>`
   *  to give a page a contextual back/title/actions configuration instead. */
  mobileTopBar?: ReactNode;
  /** How the mobile Explore search bar's bottom gradient behaves. Default `"scroll"` — it fades in
   *  once content passes under the bar. Pass `"always"` on pages the frame itself never scrolls
   *  (full-bleed map + results sheet), where a scroll-driven gradient could never appear. Ignored
   *  when `mobileTopBar` overrides the bar or the route isn't an Explore section. */
  mobileTopBarShadow?: "scroll" | "always";
  /** Cap on the default `Frame.Content` width. Default `xlarge` (= max-w-container).
   *  Pages with split-pane layouts that should fill the viewport width can pass `full`. */
  contentMaxWidth?: FrameContentMaxWidth;
  /** Whether the default `Frame.Content` uses responsive horizontal padding. Default `true`.
   *  Pass `false` for pages that manage their own internal padding (split-pane search
   *  surfaces, immersive hero pages). */
  contentPadded?: boolean;
  /** Whether the default `Frame.Content` fills the viewport and owns its own scrolling — see
   *  `Frame.Content`'s `fills`. Pair with `hideFooter`. Default `false`. */
  contentFills?: boolean;
  /** Content rendered in the top bar's `bottomContent` slot — same node is forwarded
   *  to both the mobile and desktop top bars. Common uses: `<TopBarBorder />` for
   *  visual separation on split-pane pages, or `<TopBarProgress />` on wizard flows. */
  topBarBottomContent?: ReactNode;
  /** Override top-bar stickiness. Defaults to non-sticky when a `background` is set (so the
   *  decoration scrolls with the page), sticky otherwise. Pass `false` to make the desktop top
   *  bar scroll away on pages where a pinned header competes with page chrome (e.g. the product
   *  page's floating action pill). Mobile stays sticky regardless — see Frame's `stickyTopBar`. */
  stickyTopBar?: boolean;
  /** Render the default footer edge-to-edge (no rounded corners, no top margin). Use on
   *  split-view / full-width pages so the footer aligns with the viewport edges. Default
   *  `false` — keeps the rounded "card" footer used on standard pages. */
  footerFullBleed?: boolean;
  /** The mobile bottom-bar tabs appear by default only on root routes (the tab destinations
   *  and their sub-sections — see `isRootRoute`); detail/flow pages get the full viewport.
   *  Set `hideBottomBar` to force the bar off even on a root route — use on full-screen
   *  surfaces like the messages conversation view where the tab strip would steal vertical
   *  space and compete with page-specific chrome. Default `false`. */
  hideBottomBar?: boolean;
  /** Force the mobile bottom-bar tabs on for a page the route table doesn't classify as a
   *  root. Escape hatch — prefer adding the route to `isRootRoute`. Ignored when
   *  `hideBottomBar` is set. Default `false`. */
  showBottomBar?: boolean;
  /** Hide the desktop top-bar centre nav (Programs / Destinations / Marketplace, or
   *  Listings / Payouts in creator mode). Use on focused single-purpose surfaces
   *  (messages, error pages, immersive flows) where the global nav competes with the
   *  page's own chrome. Default `false`. */
  hideTopBarNav?: boolean;
  /** Suppress the desktop footer (locale / currency picker). Use on focused single-purpose
   *  surfaces (editors, messages) where the locale row would compete with page chrome
   *  like a sticky save bar. Default `false`. */
  hideFooter?: boolean;
  /** Called when the recipient's unread-message count changes on the shared
   *  NotificationChannel. Messaging pages pass this to live-refresh their
   *  conversation list, reusing the single PageFrame-owned subscription. */
  onUnreadChanged?: () => void;
  /** Page content — rendered inside `Frame.Content`. */
  children: ReactNode;
}

/**
 * Container layer that wires Inertia state into Frame. Every authenticated page in the app
 * renders inside `<PageFrame>` (or a domain frame that wraps it). PageFrame owns:
 *
 * - reading `usePage<SharedProps>()` for current_user, current_account, accounts, flash, etc.
 * - default Frame.TopBar — desktop top bar with mode-aware nav, mobile top bar with messages action
 * - default Frame.BottomBar — mode-aware tab set
 * - default flash alert rendering inside the default Frame.Content
 * - default Frame.Footer (locale/currency) on desktop only
 * - locale modal wired into Frame.Overlay
 * - logout / language switching actions
 *
 * Slot pass-through: any direct child that's a `<Frame.*>` slot (e.g. `<Frame.LeftPane>`,
 * `<Frame.RightPane>`, `<Frame.Footer>`) is forwarded to the inner Frame as a sibling slot,
 * not nested inside the default Content. Non-slot children are wrapped in the default
 * `<Frame.Content>` (alongside flash alerts). If a consumer passes their own `<Frame.Content>`,
 * that overrides the default and they're responsible for rendering alerts themselves.
 */
export function PageFrame({
  background,
  children,
  contentFills = false,
  contentMaxWidth = "xlarge",
  contentPadded = true,
  mode = "explorer",
  mobileTopBar,
  mobileTopBarShadow = "scroll",
  stickyTopBar,
  topBarBottomContent,
  footerFullBleed = false,
  hideBottomBar = false,
  showBottomBar = false,
  hideTopBarNav = false,
  hideFooter = false,
  onUnreadChanged,
}: PageFrameProps) {
  const { slotChildren, contentChildren, hasContentSlot } = partitionFrameSlots(children);
  const { alerts, dismissAlert } = useFlashAlerts();
  const accountSwitcher = useAccountSwitcher();
  const { notifications, unseenCount, markSeen } = useNotificationChannel({ onUnreadChanged });
  const handleLogout = () => router.delete(sessionPath());

  const page = usePage<SharedProps>();
  const { locale, currency, suggested_locales, exchange_rates, stripe_connected, current_user, unread_messages_count } =
    page.props;
  const currentPath = page.url.split("?")[0] ?? page.url;
  // Cosmetic gate for the search overlay; the destination section pages enforce `:active` server-side.
  const isActiveUser = current_user?.status === "active";

  // The bottom tab bar is global navigation: show it on root routes (tab destinations and
  // their sub-sections), not on detail/flow pages. `hideBottomBar`/`showBottomBar` are
  // per-page overrides; `hideBottomBar` wins.
  const onRootRoute = isRootRoute(currentPath, { mode, isAuthenticated: accountSwitcher != null });
  const showBottomBarTabs = hideBottomBar ? false : showBottomBar || onRootRoute;

  // Pure assignment to module-level variables — safe in render phase.
  // Must be synchronous to avoid a first-render flash showing USD before
  // the user's configured currency takes effect.
  configureDisplayCurrency(currency, exchange_rates, locale);

  const [isLocaleModalOpen, setIsLocaleModalOpen] = useState(false);
  const [localeModalTab, setLocaleModalTab] = useState<LocaleModalTab>("language");
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const openLocaleModal = useCallback((tab: LocaleModalTab) => {
    setLocaleModalTab(tab);
    setIsLocaleModalOpen(true);
  }, []);

  const handleLocaleSelect = useCallback((newLocale: string) => {
    setIsLocaleModalOpen(false);
    router.patch(
      preferencesPath(),
      { locale: newLocale },
      {
        onSuccess: () => {
          // Full reload needed — t() is not reactive, Inertia navigation can't re-evaluate translations.
          // Replace (not reload) so the browser doesn't restore the previous scroll position.
          const path = globalThis.location.pathname;
          globalThis.location.replace(path);
        },
      },
    );
  }, []);

  const handleCurrencySelect = useCallback((newCurrency: string) => {
    setIsLocaleModalOpen(false);
    router.patch(preferencesPath(), { currency: newCurrency }, { preserveState: false });
  }, []);

  const handleNotificationOpen = () => {
    if (unseenCount > 0) {
      // markSeen optimistically clears the badge and persists via the
      // seen-marker endpoint, then resyncs the count from the server.
      markSeen();
    }
  };

  const handleNotificationAction = (action: NotificationAction) => {
    router[action.method](action.href);
  };

  const handleLanguagesAndCurrency = () => openLocaleModal("language");

  // A full-bleed `background` flips both top-bar bits together: paint transparent so the
  // decoration bleeds through, and stop sticking on `md+` so content doesn't slide behind it.
  const hasBackground = !!background;
  // Default stickiness derives from `background`; an explicit `stickyTopBar` prop overrides it.
  const stickyResolved = stickyTopBar ?? !hasBackground;

  return (
    <Frame>
      {background ? <Frame.Background>{background}</Frame.Background> : null}

      <Frame.TopBar sticky={stickyResolved}>
        <DefaultTopBar
          accountSwitcher={accountSwitcher}
          bottomContent={topBarBottomContent}
          currentPath={currentPath}
          currentUrl={page.url}
          hideNav={hideTopBarNav}
          mobileTopBarOverride={mobileTopBar}
          mobileTopBarShadow={mobileTopBarShadow}
          mode={mode}
          onOpenSearch={current_user ? () => setIsSearchOpen(true) : () => router.visit(loginPath())}
          unseenCount={unseenCount}
          unreadMessagesCount={unread_messages_count ?? 0}
          notificationBell={
            accountSwitcher ? (
              <NotificationBell
                hasUnseen={unseenCount > 0}
                notifications={notifications}
                onAction={handleNotificationAction}
                onOpen={handleNotificationOpen}
              />
            ) : undefined
          }
          loginHref={loginPath()}
          stripeConnected={stripe_connected}
          transparent={hasBackground}
          onLanguagesAndCurrency={handleLanguagesAndCurrency}
          onLogout={handleLogout}
        />
      </Frame.TopBar>

      {hasContentSlot ? null : (
        <Frame.Content fills={contentFills} maxWidth={contentMaxWidth} padded={contentPadded}>
          {alerts.map((alert) => (
            <Alert
              key={alert.id}
              status={alert.status}
              title={alert.title}
              description={alert.description}
              onClose={dismissAlert}
            />
          ))}
          {contentChildren}
        </Frame.Content>
      )}

      {hideFooter ? null : (
        <Frame.Footer>
          <div className={footerFullBleed ? "hidden md:block" : "hidden md:block md:px-8"}>
            <Footer
              fullBleed={footerFullBleed}
              localeLabel={getLocaleLabel(locale)}
              currencyLabel={getCurrencyLabel(currency)}
              onLocalePress={() => openLocaleModal("language")}
              onCurrencyPress={() => openLocaleModal("currency")}
            />
          </div>
        </Frame.Footer>
      )}

      {showBottomBarTabs ? (
        <Frame.BottomBar variant="floating">
          <AppBottomBar mode={mode} />
        </Frame.BottomBar>
      ) : null}

      <Frame.Overlay>
        {isExploreSection(currentPath) && (
          <ExploreSearchOverlay
            currentPath={currentPath}
            isActive={isActiveUser}
            isOpen={isSearchOpen}
            onOpenChange={setIsSearchOpen}
          />
        )}

        <LocaleModal
          isOpen={isLocaleModalOpen}
          onOpenChange={setIsLocaleModalOpen}
          currentLocale={locale}
          currentCurrency={currency}
          suggestedLocales={suggested_locales}
          defaultTab={localeModalTab}
          onLocaleSelect={handleLocaleSelect}
          onCurrencySelect={handleCurrencySelect}
        />
      </Frame.Overlay>

      {/* Consumer slots come last so Frame's last-wins iteration lets them override defaults. */}
      {slotChildren}
    </Frame>
  );
}

/**
 * Split direct PageFrame children into Frame slot elements (forwarded to the inner Frame
 * as siblings) and non-slot content (wrapped in the default Frame.Content).
 */
function partitionFrameSlots(children: ReactNode): {
  slotChildren: ReactElement[];
  contentChildren: ReactNode[];
  hasContentSlot: boolean;
} {
  const slotChildren: ReactElement[] = [];
  const contentChildren: ReactNode[] = [];
  let hasContentSlot = false;

  Children.forEach(children, (child) => {
    if (isValidElement(child) && isFrameSlot(child.type)) {
      slotChildren.push(child);
      if (child.type === Frame.Content) hasContentSlot = true;
    } else {
      contentChildren.push(child);
    }
  });

  return { slotChildren, contentChildren, hasContentSlot };
}

const FRAME_SLOT_TYPES: ReadonlySet<unknown> = new Set([
  Frame.Background,
  Frame.TopBar,
  Frame.LeftPane,
  Frame.Content,
  Frame.RightPane,
  Frame.Footer,
  Frame.BottomBar,
  Frame.Alerts,
  Frame.Overlay,
]);

function isFrameSlot(type: unknown): boolean {
  return FRAME_SLOT_TYPES.has(type);
}

interface DefaultTopBarProps {
  accountSwitcher?: AccountSwitcherProps;
  bottomContent?: ReactNode;
  currentPath: string;
  /** Full URL incl. query — the mobile Explore search pill reads its applied-search summary from it. */
  currentUrl: string;
  /** Opens the mobile Explore search overlay (pill press). */
  onOpenSearch: () => void;
  hideNav?: boolean;
  mobileTopBarOverride?: ReactNode;
  mobileTopBarShadow: "scroll" | "always";
  mode: "explorer" | "creator";
  notificationBell?: ReactNode;
  unseenCount: number;
  unreadMessagesCount: number;
  loginHref: string;
  stripeConnected?: boolean;
  /** When true, the desktop top bar paints with a transparent background so a Frame.Background
   *  decoration (e.g. the swoosh on profile pages) bleeds through. Pair with `Frame.TopBar
   *  sticky={false}` on those pages so the bar scrolls away and content never slides behind it. */
  transparent: boolean;
  onLanguagesAndCurrency: () => void;
  onLogout: () => void;
}

function DefaultTopBar({
  accountSwitcher,
  bottomContent,
  currentPath,
  currentUrl,
  hideNav = false,
  mobileTopBarOverride,
  mobileTopBarShadow,
  mode,
  notificationBell,
  onOpenSearch,
  unseenCount,
  unreadMessagesCount,
  loginHref,
  stripeConnected = false,
  transparent,
  onLanguagesAndCurrency,
  onLogout,
}: DefaultTopBarProps) {
  const { openMessenger } = useIntercom();
  const currentAccount = accountSwitcher?.currentAccount;
  const triggerAvatar = currentAccount ? (
    <AccountAvatar account={currentAccount} className="border-2 border-white shadow-sm" shape="circle" size="40" />
  ) : (
    <PersonAvatar
      className="border-2 border-white shadow-sm"
      color="green"
      shape="circle"
      size="40"
      person={{ initial: accountSwitcher?.userInitial ?? undefined }}
    />
  );

  return (
    <>
      <div className="md:hidden">
        {mobileTopBarOverride ??
          (isExploreSection(currentPath) ? (
            // The Explore home and its browse sections get a search-centric bar (search pill + filters).
            <ExploreSearchTopBar
              currentPath={currentPath}
              currentUrl={currentUrl}
              onOpenSearch={onOpenSearch}
              shadow={mobileTopBarShadow}
              notificationsAction={
                accountSwitcher ? (
                  <NotificationsAction href={notificationsPath()} unseenCount={unseenCount} />
                ) : undefined
              }
            />
          ) : (
            <TopBarMobile
              root
              actions={
                accountSwitcher ? (
                  <>
                    <NotificationsAction href={notificationsPath()} unseenCount={unseenCount} />
                    <MessagesAction href={messagesPath()} unreadCount={unreadMessagesCount} />
                  </>
                ) : undefined
              }
              bottomContent={bottomContent}
            />
          ))}
      </div>
      <div className="hidden md:block">
        <TopBarWeb
          bottomContent={bottomContent}
          className={transparent ? "bg-transparent" : undefined}
          centerContent={
            hideNav ? null : mode === "creator" ? (
              <>
                <TopBarNavLink
                  aria-current={isNavActive(currentPath, creatorListingsPath()) ? "page" : undefined}
                  color="orange"
                  href={creatorListingsPath()}
                  leadIcon={<Icon name="shop" size="32" type="sticker" />}
                >
                  {t("components.frames.app_layout.nav.listings")}
                </TopBarNavLink>
                {stripeConnected && (
                  <TopBarNavLink
                    aria-current={isNavActive(currentPath, accountPayoutsPath()) ? "page" : undefined}
                    color="green"
                    href={accountPayoutsPath()}
                    leadIcon={<Icon name="share" size="32" type="sticker" />}
                  >
                    {t("components.frames.app_layout.nav.payouts")}
                  </TopBarNavLink>
                )}
              </>
            ) : (
              <>
                <TopBarNavLink
                  aria-current={isNavActive(currentPath, programsPath()) ? "page" : undefined}
                  color="pink"
                  href={programsPath()}
                  leadIcon={<Icon name="location" size="32" type="sticker" />}
                >
                  {t("components.frames.app_layout.nav.programs")}
                </TopBarNavLink>
                <TopBarNavLink
                  aria-current={isNavActive(currentPath, destinationsPath()) ? "page" : undefined}
                  color="green"
                  href={destinationsPath()}
                  leadIcon={<Icon name="map" size="32" type="sticker" />}
                >
                  {t("components.frames.app_layout.nav.destinations")}
                </TopBarNavLink>
                <TopBarNavLink
                  aria-current={isNavActive(currentPath, marketplaceIndexPath()) ? "page" : undefined}
                  color="orange"
                  href={marketplaceIndexPath()}
                  leadIcon={<Icon name="shop" size="32" type="sticker" />}
                >
                  {t("components.frames.app_layout.nav.marketplace")}
                </TopBarNavLink>
              </>
            )
          }
          endContent={
            accountSwitcher ? (
              <>
                {notificationBell}
                <MenuTrigger>
                  <Button
                    aria-label={t("components.frames.app_layout.profile")}
                    className="relative inline-flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full outline-none focus-visible:shadow-focus-ring"
                  >
                    {triggerAvatar}
                    {unreadMessagesCount > 0 && (
                      <span className="absolute right-0 top-0 size-2.5 rounded-full bg-red-400 ring-2 ring-white" />
                    )}
                  </Button>
                  <DropMenuPopover placement="bottom end">
                    <AppMenu
                      accountSwitcher={accountSwitcher}
                      mode={mode}
                      onLanguagesAndCurrency={onLanguagesAndCurrency}
                      onGetHelp={openMessenger}
                      onLogout={onLogout}
                      unreadMessagesCount={unreadMessagesCount}
                    />
                  </DropMenuPopover>
                </MenuTrigger>
              </>
            ) : (
              <Link
                className={outlineButton({ border: "default", shape: "pill", size: "40", className: "no-underline" })}
                href={loginHref}
              >
                {t("components.frames.app_layout.log_in")}
              </Link>
            )
          }
        />
      </div>
    </>
  );
}
